import { loadJsPDF } from "./pdfEngines";
import { addPreviewWatermarkJsPdf } from "./previewWatermark";
import { generateUkTemplateH } from "./ukTemplateH";
import { generateUkTemplateS } from "./ukTemplateS";
import { periodsPerYear as ukPeriodsPerYear, periodDays as ukPeriodDays, periodEnd as ukPeriodEnd, addPeriods as ukAddPeriods, ytdPeriods as ukYtdPeriods, defaultHours as ukDefaultHours, taxYearStart as ukTaxYearStart, taxPeriod as ukTaxPeriod } from "./ukPayPeriods";
import { calculateUkTaxes } from "./ukTaxRates";
import { ukOverrides } from "./taxRateDefaults";
import { renderLayout, fetchPublishedLayout, fetchCustomLayout, isCustomTemplate } from "./layoutEngine";
import { loadPdfJs } from "./pdfEngines";

// A ready-to-render sample stub for card thumbnails (see SAMPLE_PAYSTUB_DATA).
export const SAMPLE_UK_PAYSLIP_DATA = {
  name: "Emma Clarke", niNumber: "AB 12 34 56 C", bank: "4821", bankName: "Barclays",
  address: "14 Victoria Road", city: "Manchester", postcode: "M1 4BT",
  company: "Northgate Consulting Ltd", companyAddress: "22 Deansgate",
  companyCity: "Manchester", companyPostcode: "M3 1AB",
  companyPhone: "0161 496 0123",
  hireDate: "2022-01-04", startDate: "2025-01-01", endDate: "2025-01-31",
  rate: "18.50", payFrequency: "monthly", payDay: "Friday",
  payType: "hourly", workerType: "employee", annualSalary: "",
  employeeId: "EMP001", companyCode: "", locDept: "", checkNumber: "",
  taxCode: "1257L", niCategory: "A", studentLoanPlan: "none",
  hoursList: "162.5", overtimeList: "0", commissionList: "0",
  startDateList: "2025-01-01", endDateList: "2025-01-31", payDateList: "2025-01-31",
};

// Set up pdf.js worker using unpkg CDN with correct version

// PDF metadata configuration per template (matching real document signatures)
const TEMPLATE_METADATA = {
  'template-a': { title: 'Gusto', creator: 'wkhtmltopdf 0.12.6.1', producer: 'Qt 4.8.7' },
  'template-b': { title: 'ADP', creator: 'wkhtmltopdf 0.12.6.1', producer: 'Qt 4.8.7' },
  'template-c': { creator: 'Workday HCM', producer: 'Workday PDF Engine' },  // No title
  'template-h': { creator: 'wkhtmltopdf 0.12.6.1', producer: 'Qt 4.8.7' },  // No title (OnPay)
  'template-s': { creator: 'Sage 50 Payroll', producer: 'Sage Report Designer' },  // UK Sage-style payslip
};

// Apply template-specific PDF metadata
function applyPdfMetadata(doc, template) {
  // Custom templates carry their own metadata (applied by renderLayout).
  if (isCustomTemplate(template)) return;
  const metadata = TEMPLATE_METADATA[template] || TEMPLATE_METADATA['template-h'];
  const props = { creator: metadata.creator, producer: metadata.producer };
  if (metadata.title) {
    props.title = metadata.title;
  }
  doc.setProperties(props);
}

// Convert PDF to image using pdf.js
async function convertPdfToImage(pdfDataUrl) {
  const base64Data = pdfDataUrl.split(',')[1];
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  
  const loadingTask = (await loadPdfJs()).getDocument({ data: bytes.buffer });
  const pdf = await loadingTask.promise;
  const page = await pdf.getPage(1);
  
  const scale = 2;
  const viewport = page.getViewport({ scale });
  
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  
  await page.render({
    canvasContext: context,
    viewport: viewport
  }).promise;
  
  return canvas.toDataURL('image/png', 0.9);
}

// Helper to calculate next weekday
const DAY_MAP = {
  Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6,
};

function nextWeekday(date, weekday) {
  const result = new Date(date);
  const target = DAY_MAP[weekday];
  if (target === undefined) return result;
  while (result.getDay() !== target) {
    result.setDate(result.getDate() + 1);
  }
  return result;
}



export async function generateUkPreviewPDF(formData, template) {
  try {
    const doc = new (await loadJsPDF())({ orientation: "portrait", unit: "pt", format: "letter" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;

    // Parse form data
    const rate = parseFloat(formData.rate) || 0;
    const annualSalary = parseFloat(formData.annualSalary) || 0;
    const payFrequency = formData.payFrequency || "biweekly";
    const periodLength = ukPeriodDays(payFrequency) || 30; // a month's payslips step by calendar month (ukPayPeriods)
    const defaultHours = ukDefaultHours(payFrequency);
    const payDay = formData.payDay || "Friday";
    const payType = formData.payType || "hourly";
    const workerType = formData.workerType || "employee";
    const isContractor = workerType === "contractor";
    const taxCode = formData.taxCode || "1257L";
    
    const periodsPerYear = ukPeriodsPerYear(payFrequency);

    // Get hours
    const hours = formData.hoursList 
      ? parseFloat(formData.hoursList.split(",")[0]) || defaultHours 
      : defaultHours;
    const overtime = isContractor ? 0 : (formData.overtimeList 
      ? parseFloat(formData.overtimeList.split(",")[0]) || 0 
      : 0);
    const commission = formData.commissionList 
      ? parseFloat(formData.commissionList.split(",")[0]) || 0 
      : 0;

    // Parse custom dates for each period
    const startDateArray = (formData.startDateList || "")
      .split(",")
      .map((d) => d.trim())
      .filter(d => d);
    const endDateArray = (formData.endDateList || "")
      .split(",")
      .map((d) => d.trim())
      .filter(d => d);
    const payDateArray = (formData.payDateList || "")
      .split(",")
      .map((d) => d.trim())
      .filter(d => d);

    // Calculate period dates - use custom dates if available
    const startDate = startDateArray[0] 
      ? new Date(startDateArray[0] + 'T12:00:00') 
      : (formData.startDate ? new Date(formData.startDate) : new Date());
    const endDate = endDateArray[0] 
      ? new Date(endDateArray[0] + 'T12:00:00')
      : (formData.endDate ? new Date(formData.endDate) : ukPeriodEnd(startDate, payFrequency));
    // Use custom pay date if available, otherwise calculate from end date
    const payDate = payDateArray[0] 
      ? new Date(payDateArray[0] + 'T12:00:00')
      : nextWeekday(endDate, payDay);
    const hireDate = formData.hireDate ? new Date(formData.hireDate) : startDate;

    // Calculate YTD pay periods
    const ytdPayPeriods = ukYtdPeriods(hireDate, endDate, payFrequency);

    // Calculate earnings
    let regularPay, overtimePay, grossPay;
    if (payType === "salary") {
      regularPay = annualSalary / periodsPerYear;
      overtimePay = 0;
      grossPay = regularPay + commission;
    } else {
      regularPay = rate * hours;
      overtimePay = rate * 1.5 * overtime;
      grossPay = regularPay + overtimePay + commission;
    }

    // Calculate UK deductions
    let payeTax = 0, nationalInsurance = 0, studentLoan = 0, totalTax = 0;
if (!isContractor) {
  const taxes = calculateUkTaxes(grossPay, payFrequency, {
    taxCode, niCategory: formData.niCategory, studentLoanPlan: formData.studentLoanPlan, overrides: ukOverrides(formData),
  });
  payeTax = taxes.payeTax;
  nationalInsurance = taxes.nationalInsurance;
  studentLoan = taxes.studentLoan;
  totalTax = taxes.totalTax;
}

    // Process deductions
    const deductions = formData.deductions || [];
    let totalDeductions = 0;
    const deductionsData = deductions.map(d => {
      const currentAmount = d.isPercentage ? (grossPay * parseFloat(d.amount) / 100) : parseFloat(d.amount) || 0;
      totalDeductions += currentAmount;
      return { ...d, currentAmount, ytdAmount: currentAmount * ytdPayPeriods };
    });

    // Process contributions
    const contributions = formData.contributions || [];
    let totalContributions = 0;
    const contributionsData = contributions.map(c => {
      const currentAmount = c.isPercentage ? (grossPay * parseFloat(c.amount) / 100) : parseFloat(c.amount) || 0;
      totalContributions += currentAmount;
      return { ...c, currentAmount, ytdAmount: currentAmount * ytdPayPeriods };
    });

    const netPay = grossPay - totalTax - totalDeductions;
    const totalHours = hours + overtime;

    // Prepare template data
    const templateData = {
      formData,
      hours,
      overtime,
      commission,
      regularPay,
      overtimePay,
      grossPay,
      payeTax,
      nationalInsurance,
      studentLoan,
      totalTax,
      netPay,
      rate,
      startDate,
      endDate,
      payDate,
      payFrequency,
      stubNum: 1,
      totalStubs: 1,
      ytdRegularPay: regularPay * ytdPayPeriods,
      ytdOvertimePay: overtimePay * ytdPayPeriods,
      ytdCommission: commission * ytdPayPeriods,
      ytdGrossPay: grossPay * ytdPayPeriods,
      ytdPayeTax: payeTax * ytdPayPeriods,
      ytdNationalInsurance: nationalInsurance * ytdPayPeriods,
      ytdStudentLoan: studentLoan * ytdPayPeriods,
      ytdTotalTax: totalTax * ytdPayPeriods,
      ytdNetPay: netPay * ytdPayPeriods,
      ytdHours: totalHours * ytdPayPeriods,
      payType,
      workerType,
      isContractor,
      annualSalary,
      deductionsData,
      totalDeductions,
      contributionsData,
      totalContributions,
      ytdDeductions: totalDeductions * ytdPayPeriods,
      ytdContributions: totalContributions * ytdPayPeriods,
      ytdPayPeriods,
      logoDataUrl: formData.logoDataUrl || formData.companyLogo,
      taxCode,
      niNumber: formData.niNumber || "",
      niCategory: formData.niCategory || "A",
      taxPeriod: ukTaxPeriod(payDate, payFrequency).label,
      taxYear: ukTaxPeriod(payDate, payFrequency).taxYear,
      isPreview: true,
      // Absence plans for Template C (Workday)
      absencePlansData: (formData.absencePlans || []).map(plan => ({
        description: plan.description || "PTO Plan",
        accrued: plan.accrued || "0",
        reduced: plan.reduced || "0"
      })),
      // Employer benefits for Template C (Workday)
      employerBenefitsData: (formData.employerBenefits || []).map(b => {
        let amount;
        if (b.type === 'pension_match') {
          // Calculate RRSP match based on employee contribution
          const employeePension = contributionsData.find(c => c.type === 'pension');
          if (employeePension) {
            const matchUpTo = parseFloat(b.matchUpTo) || 6;
            const matchPercent = parseFloat(b.matchPercent) || 50;
            const maxMatchableAmount = grossPay * (matchUpTo / 100);
            const matchableAmount = Math.min(employeePension.amount, maxMatchableAmount);
            amount = matchableAmount * (matchPercent / 100);
          } else {
            amount = 0;
          }
        } else {
          amount = b.isPercentage ? (grossPay * parseFloat(b.amount) / 100) : parseFloat(b.amount) || 0;
        }
        return { name: b.name || "Employer Benefit", type: b.type, currentAmount: amount };
      }),
      totalEmployerBenefits: (formData.employerBenefits || []).reduce((sum, b) => {
        let amount;
        if (b.type === 'pension_match') {
          const employeePension = contributionsData.find(c => c.type === 'pension');
          if (employeePension) {
            const matchUpTo = parseFloat(b.matchUpTo) || 6;
            const matchPercent = parseFloat(b.matchPercent) || 50;
            const maxMatchableAmount = grossPay * (matchUpTo / 100);
            const matchableAmount = Math.min(employeePension.amount, maxMatchableAmount);
            amount = matchableAmount * (matchPercent / 100);
          } else {
            amount = 0;
          }
        } else {
          amount = b.isPercentage ? (grossPay * parseFloat(b.amount) / 100) : parseFloat(b.amount) || 0;
        }
        return sum + amount;
      }, 0)
    };

    // Generate selected template
    if (isCustomTemplate(template)) {
      const customLayout = await fetchCustomLayout(template);
      if (customLayout) renderLayout(doc, customLayout, templateData, "uk-payslip");
      else await generateUkTemplateH(doc, templateData, pageWidth, pageHeight, margin);
    } else if (template === "template-s") {
      await generateUkTemplateS(doc, templateData, pageWidth, pageHeight, margin);
    } else {
      await generateUkTemplateH(doc, templateData, pageWidth, pageHeight, margin);
    }

    // Add watermark
    await addPreviewWatermarkJsPdf(doc, pageWidth, pageHeight);

    // Apply metadata before output
    applyPdfMetadata(doc, template);

    // Apply metadata before output
    const pdfDataUrl = doc.output('dataurlstring');
    return pdfDataUrl;
  } catch (error) {
    console.error("Error generating Uk preview:", error);
    throw error;
  }
}

// Helper function to generate a single UK payslip preview
async function generateSingleUkStubPreview(formData, template, stubIndex, totalStubs, commonData) {
  const {
    rate, annualSalary, payFrequency, periodLength, defaultHours, payDay,
    payType, workerType, isContractor, taxCode, periodsPerYear, 
    hoursArray, overtimeArray, commissionArray, startDateArray, endDateArray, payDateArray, hireDate
  } = commonData;

  const doc = new (await loadJsPDF())({ orientation: "portrait", unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;

  // Calculate start date for this stub
  let startDate;
  if (startDateArray[stubIndex]) {
    startDate = new Date(startDateArray[stubIndex] + 'T12:00:00');
  } else if (formData.startDate) {
    startDate = new Date(formData.startDate);
    startDate = ukAddPeriods(startDate, payFrequency, stubIndex);
  } else {
    startDate = new Date(hireDate);
    startDate = ukAddPeriods(startDate, payFrequency, stubIndex);
  }

  // Get hours for this stub
  const hours = hoursArray[stubIndex] !== undefined ? hoursArray[stubIndex] : defaultHours;
  const overtime = isContractor ? 0 : (overtimeArray[stubIndex] || 0);
  const commission = commissionArray[stubIndex] || 0;

  // Calculate earnings
  let regularPay, overtimePay, grossPay;
  if (payType === "salary") {
    regularPay = annualSalary / periodsPerYear;
    overtimePay = 0;
    grossPay = regularPay + commission;
  } else {
    regularPay = rate * hours;
    overtimePay = rate * 1.5 * overtime;
    grossPay = regularPay + overtimePay + commission;
  }

  // Calculate end date
  const endDate = endDateArray[stubIndex] 
    ? new Date(endDateArray[stubIndex] + 'T12:00:00')
    : (() => {
        return ukPeriodEnd(startDate, payFrequency);
      })();

  // Calculate pay date
  const payDate = payDateArray[stubIndex] 
    ? new Date(payDateArray[stubIndex] + 'T12:00:00')
    : nextWeekday(new Date(endDate), payDay);

  // Calculate YTD
  const ytdPayPeriods = ukYtdPeriods(hireDate, endDate, payFrequency);

  // Calculate UK deductions
  let payeTax = 0, nationalInsurance = 0, studentLoan = 0, totalTax = 0;
if (!isContractor) {
  const taxes = calculateUkTaxes(grossPay, payFrequency, {
    taxCode, niCategory: formData.niCategory, studentLoanPlan: formData.studentLoanPlan, overrides: ukOverrides(formData),
  });
  payeTax = taxes.payeTax;
  nationalInsurance = taxes.nationalInsurance;
  studentLoan = taxes.studentLoan;
  totalTax = taxes.totalTax;
}

  // Process deductions
  const deductions = formData.deductions || [];
  let totalDeductions = 0;
  const deductionsData = deductions.map(d => {
    const currentAmount = d.isPercentage ? (grossPay * parseFloat(d.amount) / 100) : parseFloat(d.amount) || 0;
    totalDeductions += currentAmount;
    return { ...d, currentAmount, ytdAmount: currentAmount * ytdPayPeriods };
  });

  // Process contributions
  const contributions = formData.contributions || [];
  let totalContributions = 0;
  const contributionsData = contributions.map(c => {
    const currentAmount = c.isPercentage ? (grossPay * parseFloat(c.amount) / 100) : parseFloat(c.amount) || 0;
    totalContributions += currentAmount;
    return { ...c, currentAmount, ytdAmount: currentAmount * ytdPayPeriods };
  });

  const netPay = grossPay - totalTax - totalDeductions;
  const totalHours = hours + overtime;

  const templateData = {
    formData,
    hours,
    overtime,
    commission,
    regularPay,
    overtimePay,
    grossPay,
    payeTax,
    nationalInsurance,
    studentLoan,
    totalTax,
    netPay,
    rate,
    startDate,
    endDate,
    payDate,
    payFrequency,
    stubNum: stubIndex + 1,
    totalStubs,
    ytdRegularPay: regularPay * ytdPayPeriods,
    ytdOvertimePay: overtimePay * ytdPayPeriods,
    ytdCommission: commission * ytdPayPeriods,
    ytdGrossPay: grossPay * ytdPayPeriods,
    ytdPayeTax: payeTax * ytdPayPeriods,
    ytdNationalInsurance: nationalInsurance * ytdPayPeriods,
    ytdStudentLoan: studentLoan * ytdPayPeriods,
    ytdTotalTax: totalTax * ytdPayPeriods,
    ytdNetPay: netPay * ytdPayPeriods,
    ytdHours: totalHours * ytdPayPeriods,
    payType,
    workerType,
    isContractor,
    annualSalary,
    deductionsData,
    totalDeductions,
    contributionsData,
    totalContributions,
    ytdDeductions: totalDeductions * ytdPayPeriods,
    ytdContributions: totalContributions * ytdPayPeriods,
    ytdPayPeriods,
    logoDataUrl: formData.logoDataUrl || formData.companyLogo,
    taxCode,
    niNumber: formData.niNumber || "",
    niCategory: formData.niCategory || "A",
    taxPeriod: ukTaxPeriod(payDate, payFrequency).label,
    taxYear: ukTaxPeriod(payDate, payFrequency).taxYear,
    isPreview: true,
    absencePlansData: (formData.absencePlans || []).map(plan => ({
      description: plan.description || "PTO Plan",
      accrued: plan.accrued || "0",
      reduced: plan.reduced || "0"
    })),
    employerBenefitsData: (formData.employerBenefits || []).map(b => {
      let amount;
      if (b.type === 'pension_match') {
        const employeePension = contributionsData.find(c => c.type === 'pension');
        if (employeePension) {
          const matchUpTo = parseFloat(b.matchUpTo) || 6;
          const matchPercent = parseFloat(b.matchPercent) || 50;
          const maxMatchableAmount = grossPay * (matchUpTo / 100);
          const matchableAmount = Math.min(employeePension.amount, maxMatchableAmount);
          amount = matchableAmount * (matchPercent / 100);
        } else {
          amount = 0;
        }
      } else {
        amount = b.isPercentage ? (grossPay * parseFloat(b.amount) / 100) : parseFloat(b.amount) || 0;
      }
      return { name: b.name || "Employer Benefit", type: b.type, currentAmount: amount };
    }),
    totalEmployerBenefits: (formData.employerBenefits || []).reduce((sum, b) => {
      let amount;
      if (b.type === 'pension_match') {
        const employeePension = contributionsData.find(c => c.type === 'pension');
        if (employeePension) {
          const matchUpTo = parseFloat(b.matchUpTo) || 6;
          const matchPercent = parseFloat(b.matchPercent) || 50;
          const maxMatchableAmount = grossPay * (matchUpTo / 100);
          const matchableAmount = Math.min(employeePension.amount, maxMatchableAmount);
          amount = matchableAmount * (matchPercent / 100);
        } else {
          amount = 0;
        }
      } else {
        amount = b.isPercentage ? (grossPay * parseFloat(b.amount) / 100) : parseFloat(b.amount) || 0;
      }
      return sum + amount;
    }, 0)
  };

  // Generate selected template
  if (isCustomTemplate(template)) {
      const customLayout = await fetchCustomLayout(template);
      if (customLayout) renderLayout(doc, customLayout, templateData, "uk-payslip");
      else await generateUkTemplateH(doc, templateData, pageWidth, pageHeight, margin);
    } else if (template === "template-s") {
      await generateUkTemplateS(doc, templateData, pageWidth, pageHeight, margin);
    } else {
      await generateUkTemplateH(doc, templateData, pageWidth, pageHeight, margin);
    }

  // Add watermark
  await addPreviewWatermarkJsPdf(doc, pageWidth, pageHeight);

  // Apply metadata before output
  applyPdfMetadata(doc, template);

  const pdfDataUrl = doc.output('dataurlstring');
  return pdfDataUrl;
}

// Generate all Uk preview PDFs as an array of base64 image URLs
export async function generateAllUkPreviewPDFs(formData, template = 'template-h', numStubs = 1) {
  try {
    const rate = parseFloat(formData.rate) || 0;
    const annualSalary = parseFloat(formData.annualSalary) || 0;
    const payFrequency = formData.payFrequency || "biweekly";
    const periodLength = ukPeriodDays(payFrequency) || 30; // a month's payslips step by calendar month (ukPayPeriods)
    const defaultHours = ukDefaultHours(payFrequency);
    const payDay = formData.payDay || "Friday";
    const payType = formData.payType || "hourly";
    const workerType = formData.workerType || "employee";
    const isContractor = workerType === "contractor";
    const taxCode = formData.taxCode || "1257L";
    const periodsPerYear = ukPeriodsPerYear(payFrequency);

    const hoursArray = (formData.hoursList || "").split(",").map((h) => parseFloat(h.trim()) || 0);
    const overtimeArray = (formData.overtimeList || "").split(",").map((h) => parseFloat(h.trim()) || 0);
    const commissionArray = (formData.commissionList || "").split(",").map((c) => parseFloat(c.trim()) || 0);
    const startDateArray = (formData.startDateList || "").split(",").map((d) => d.trim()).filter(d => d);
    const endDateArray = (formData.endDateList || "").split(",").map((d) => d.trim()).filter(d => d);
    const payDateArray = (formData.payDateList || "").split(",").map((d) => d.trim()).filter(d => d);

    let hireDate = formData.hireDate
      ? new Date(formData.hireDate + 'T12:00:00')
      : (startDateArray[0] ? new Date(startDateArray[0] + 'T12:00:00')
        : (formData.startDate ? new Date(formData.startDate + 'T12:00:00') : new Date()));

    // Match the US generators: a hire date after the first period ends is
    // contradictory input — anchor YTD at Jan 1 for every stub.
    const firstPeriodEnd = endDateArray[0]
      ? new Date(endDateArray[0] + 'T12:00:00')
      : (() => {
          const base = startDateArray[0] || formData.startDate;
          if (!base) return null;
          return ukPeriodEnd(new Date(base + 'T12:00:00'), payFrequency);
        })();
    if (firstPeriodEnd && hireDate > firstPeriodEnd) {
      hireDate = ukTaxYearStart(firstPeriodEnd);
    }

    const commonData = {
      rate, annualSalary, payFrequency, periodLength, defaultHours, payDay,
      payType, workerType, isContractor, taxCode, periodsPerYear,
      hoursArray, overtimeArray, commissionArray, startDateArray, endDateArray, payDateArray, hireDate
    };

    // Generate previews for all stubs
    const previews = [];
    const actualNumStubs = Math.max(1, numStubs);
    
    for (let i = 0; i < actualNumStubs; i++) {
      const preview = await generateSingleUkStubPreview(formData, template, i, actualNumStubs, commonData);
      previews.push(preview);
    }

    return previews;
  } catch (error) {
    console.error("Error generating all Uk previews:", error);
    return [null];
  }
}

// Same as generateAllUkPreviewPDFs but rasterizes each stub to a PNG
// image data URL (via pdf.js), for surfaces that render previews in an <img>
// (the app's template cards and preview modal).
export async function generateAllUkPreviewImages(formData, template = 'template-h', numStubs = 1) {
  const pdfs = await generateAllUkPreviewPDFs(formData, template, numStubs);
  const images = [];
  for (const pdf of pdfs) {
    try {
      images.push(pdf ? await convertPdfToImage(pdf) : null);
    } catch (error) {
      console.error("Error converting Uk preview to image:", error);
      images.push(null);
    }
  }
  return images;
}
